/**
 * Typed fetch wrapper for the Pulsez REST API (src/shared/api.ts is the contract).
 * Same-origin, session cookie included, JSON in and out. Every non-GET request carries
 * `Content-Type: application/json` (the Worker's CSRF guard requires it). Failures throw `ApiError`.
 */
import type { Api } from "../../shared/api";
import type { ApiError as ApiErrorBody } from "../../shared/contracts";

/** Routes the browser calls (daemon-only, WebSocket, binary, provider-only and test-only routes are excluded). */
export type ApiKey = Exclude<
  keyof Api,
  | "GET /api/files/*"
  | "GET /api/f/:token/:name"
  | "GET /api/machines/:id/vnc"
  | "GET /api/machines/connect"
  | "POST /api/machines/pair/start"
  | "POST /api/machines/pair/token"
  | "POST /api/machines/token"
  | `${string} /__e2e/${string}`
>;

type PathOf<K extends string> = K extends `${string} ${infer P}` ? P : never;
type ParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamNames<Rest>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;

export type ApiRes<K extends ApiKey> = Api[K] extends { res: infer R } ? R : never;
export type ApiBody<K extends ApiKey> = Api[K] extends { body: infer B } ? B : never;
export type ApiQuery<K extends ApiKey> = Api[K] extends { query: infer Q } ? Q : never;

type ParamsOpt<K extends ApiKey> = [ParamNames<PathOf<K>>] extends [never]
  ? { params?: undefined }
  : { params: Record<ParamNames<PathOf<K>>, string> };
type BodyOpt<K extends ApiKey> = [ApiBody<K>] extends [never] ? { body?: undefined } : { body: ApiBody<K> };
type QueryOpt<K extends ApiKey> = [ApiQuery<K>] extends [never] ? { query?: undefined } : { query?: Partial<ApiQuery<K>> };

export type ApiOptions<K extends ApiKey> = ParamsOpt<K> &
  BodyOpt<K> &
  QueryOpt<K> & {
    signal?: AbortSignal;
    /** Default true: a 401 sends the user back to /connexion. */
    redirectOn401?: boolean;
  };

/** Error thrown by `api()`. `message` is French and can be shown as is. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

/** French message for any thrown value. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Une erreur inattendue est survenue. Réessaie dans un instant.";
}

let unauthorizedHandler: (() => void) | null = null;

/** Called on any 401 (except auth routes): the session layer clears its cache and redirects. */
export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn;
}

const STATUS_CODES: Record<number, string> = {
  400: "invalid",
  401: "unauthorized",
  402: "budget",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  413: "invalid",
  415: "invalid",
  429: "rate_limited",
  503: "unavailable",
};

function fallbackMessage(status: number): string {
  if (status === 401) return "Connecte-toi pour continuer.";
  if (status === 403) return "Action non autorisée.";
  if (status === 404) return "Introuvable.";
  if (status === 402) return "Le budget est atteint pour le moment.";
  if (status === 409) return "Ça a changé entre-temps, recharge la page.";
  if (status === 429) return "Trop de tentatives. Réessaie dans un moment.";
  if (status === 503) return "Service momentanément indisponible.";
  if (status >= 500) return "Le serveur a un souci. Réessaie dans un instant.";
  return "Requête refusée.";
}

function isErrorBody(v: unknown): v is ApiErrorBody {
  return typeof v === "object" && v !== null && typeof (v as ApiErrorBody).error === "string" && typeof (v as ApiErrorBody).message === "string";
}

function buildUrl(pattern: string, params: Record<string, string> | undefined, query: Record<string, unknown> | undefined): string {
  const path = pattern.replace(/:([A-Za-z_]+)/g, (_m, name: string) => {
    const value = params?.[name];
    if (typeof value !== "string" || value === "") throw new ApiError(0, "invalid", "Paramètre manquant.");
    return encodeURIComponent(value);
  });
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

/**
 * Calls one API route. Example: `api("POST /api/missions/:id/run", { params: { id }, body: {} })`.
 * Resolves with the typed response, rejects with `ApiError` (or the AbortError when `signal` aborts).
 */
export async function api<K extends ApiKey>(key: K, ...args: {} extends ApiOptions<K> ? [ApiOptions<K>?] : [ApiOptions<K>]): Promise<ApiRes<K>> {
  const opts = (args[0] ?? {}) as {
    params?: Record<string, string>;
    body?: unknown;
    query?: Record<string, unknown>;
    signal?: AbortSignal;
    redirectOn401?: boolean;
  };
  const space = key.indexOf(" ");
  const method = key.slice(0, space);
  const pattern = key.slice(space + 1);
  const url = buildUrl(pattern, opts.params, opts.query);

  const headers: Record<string, string> = { Accept: "application/json" };
  const init: RequestInit = { method, headers, credentials: "same-origin", signal: opts.signal };
  if (method === "GET") {
    init.cache = "no-store";
  } else {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.body ?? {});
  }

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new ApiError(0, "network", "Impossible de joindre Ariston. Vérifie ta connexion.");
  }

  const text = await res.text().catch(() => "");
  let data: unknown = undefined;
  let parsed = false;
  if (text) {
    try {
      data = JSON.parse(text);
      parsed = true;
    } catch {
      parsed = false;
    }
  }

  if (!res.ok) {
    if (res.status === 401 && opts.redirectOn401 !== false && !pattern.startsWith("/api/auth/")) unauthorizedHandler?.();
    if (isErrorBody(data)) throw new ApiError(res.status, data.error, data.message);
    throw new ApiError(res.status, STATUS_CODES[res.status] ?? "error", fallbackMessage(res.status));
  }
  if (!text) return undefined as ApiRes<K>;
  // A 200 that is not JSON means the request fell through to the SPA (route missing or misrouted).
  if (!parsed) throw new ApiError(res.status, "invalid_response", "Réponse inattendue du serveur.");
  return data as ApiRes<K>;
}

/** URL of a stored file (screenshots…), served by GET /api/files/* after an ownership check. */
export function fileUrl(fileKey: string): string {
  return `/api/files/${fileKey.split("/").map(encodeURIComponent).join("/")}`;
}

/** ws:// or wss:// origin of the current page. */
export function wsOrigin(): string {
  return `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}`;
}
